(() => {
  "use strict";

  const RUNTIME_KEY = "__guruContactRuntime";
  const ROOT_SELECTOR = "[data-guru-contact-root]";
  const existingRuntime = window[RUNTIME_KEY];

  if (existingRuntime?.version === 1) {
    existingRuntime.scan(document);
    return;
  }

  existingRuntime?.destroyAll?.();

  const controllers = new Map();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const rootNodes = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  class ContactController {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.resizeObservers = [];
      this.animationFrames = new Set();
      this.carousel = null;
      this.destroyed = false;
      this.initializeCarousel();
    }

    frame(callback) {
      const frame = window.requestAnimationFrame(() => {
        this.animationFrames.delete(frame);
        if (!this.destroyed) callback();
      });
      this.animationFrames.add(frame);
      return frame;
    }

    observeResize(target, callback) {
      if (!("ResizeObserver" in window) || !target) return;
      let previousWidth = -1;
      const observer = new ResizeObserver(([entry]) => {
        const width = Math.round(entry?.contentRect?.width || 0);
        if (width === previousWidth) return;
        previousWidth = width;
        callback();
      });
      observer.observe(target);
      this.resizeObservers.push(observer);
    }

    initializeCarousel() {
      const region = this.root.querySelector("[data-guru-contact-carousel]");
      const track = region?.querySelector("[data-guru-contact-products-track]");
      const previous = region?.querySelector("[data-guru-contact-previous]");
      const next = region?.querySelector("[data-guru-contact-next]");
      if (!region || !track || !previous || !next) return;

      const state = { region, track, previous, next, updateFrame: 0 };
      this.carousel = state;

      const step = () => {
        const card = track.querySelector(":scope > .guru-product-card, :scope > .product-card");
        if (!card) return Math.max(1, track.clientWidth * 0.8);
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      };

      const update = () => {
        if (this.destroyed || !track.isConnected) return;
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = maximum <= 1 || track.scrollLeft <= 1;
        next.disabled = maximum <= 1 || track.scrollLeft >= maximum - 1;
      };

      const queueUpdate = () => {
        if (state.updateFrame) return;
        state.updateFrame = this.frame(() => {
          state.updateFrame = 0;
          update();
        });
      };

      const move = (direction) => {
        track.scrollBy({
          left: direction * step(),
          behavior: reducedMotion.matches ? "auto" : "smooth"
        });
        queueUpdate();
      };

      const signal = this.abortController.signal;
      previous.addEventListener("click", () => move(-1), { signal });
      next.addEventListener("click", () => move(1), { signal });
      track.addEventListener("scroll", queueUpdate, { passive: true, signal });
      track.addEventListener("scrollend", update, { signal });
      track.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        move(event.key === "ArrowLeft" ? -1 : 1);
      }, { signal });

      this.observeResize(track, update);
      this.frame(update);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.abortController.abort();
      this.resizeObservers.forEach((observer) => observer.disconnect());
      this.resizeObservers.length = 0;
      this.animationFrames.forEach((frame) => window.cancelAnimationFrame(frame));
      this.animationFrames.clear();
      if (this.carousel?.updateFrame) window.cancelAnimationFrame(this.carousel.updateFrame);
    }
  }

  const scan = (scope = document) => {
    rootNodes(scope).forEach((root) => {
      if (!root.isConnected || controllers.has(root)) return;
      controllers.set(root, new ContactController(root));
    });
  };

  const unload = (scope) => {
    [...controllers].forEach(([root, controller]) => {
      if (root === scope || scope?.contains?.(root) || !root.isConnected) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  };

  const onSectionLoad = (event) => scan(event.target);
  const onSectionUnload = (event) => unload(event.target);
  document.addEventListener("shopify:section:load", onSectionLoad);
  document.addEventListener("shopify:section:unload", onSectionUnload);

  const runtime = {
    version: 1,
    scan,
    unload,
    destroyAll() {
      controllers.forEach((controller) => controller.destroy());
      controllers.clear();
      document.removeEventListener("shopify:section:load", onSectionLoad);
      document.removeEventListener("shopify:section:unload", onSectionUnload);
      if (window[RUNTIME_KEY] === runtime) delete window[RUNTIME_KEY];
    }
  };

  window[RUNTIME_KEY] = runtime;
  scan(document);
})();
